import React, { useState, useEffect } from 'react';
import {
    Alert,
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    CircularProgress,
    Tooltip,
    IconButton,
    Snackbar,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useLanguageContext } from '../../hooks/useLanguageContext';
import { multilingual } from '../../multilingual';
import EditHouseModal from './EditHouseModal';
import AddDevicesModal from './AddDevicesModal';
import UsageDetails from './UsageDetails';
import VisitsManagement from './VisitsManagement';



import { getHousePeriodicConsumptions, getHouseBillingInfo, getHouseCostByPeriod } from '../../api_calls/api_calls_houses';
import {
    BUILDING_ADDRESS,
    DEMO_TODAY,
} from '../../data/mockApartments';


const API_BASE = process.env.REACT_APP_API_URL || '';

const DAYS_FULL_EN = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAYS_SHORT_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS_EN = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
const MONTHS_GR_GENITIVE = ['ΙΑΝΟΥΑΡΙΟΥ', 'ΦΕΒΡΟΥΑΡΙΟΥ', 'ΜΑΡΤΙΟΥ', 'ΑΠΡΙΛΙΟΥ', 'ΜΑΪΟΥ', 'ΙΟΥΝΙΟΥ', 'ΙΟΥΛΙΟΥ', 'ΑΥΓΟΥΣΤΟΥ', 'ΣΕΠΤΕΜΒΡΙΟΥ', 'ΟΚΤΩΒΡΙΟΥ', 'ΝΟΕΜΒΡΙΟΥ', 'ΔΕΚΕΜΒΡΙΟΥ'];

/** Parse DD-MM-YYYY to Date. Returns null if not valid. */
const parseDDMMYYYY = (str) => {
    if (!str || typeof str !== 'string') return null;
    const parts = str.trim().split(/[-/]/);
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    const d = new Date(year, month, day);
    if (d.getDate() !== day || d.getMonth() !== month || d.getFullYear() !== year) return null;
    return d;
};

/** Map API labels: day names → localized short day names; date strings (DD-MM-YYYY) → short date (e.g. "28/02"). */
const translateDayLabels = (labelList, chartDaysShort) => {
    if (!labelList?.length) return labelList;
    return labelList.map((l) => {
        const fullIdx = DAYS_FULL_EN.indexOf(l);
        if (fullIdx >= 0) return (chartDaysShort && chartDaysShort[fullIdx]) || l;
        const shortIdx = DAYS_SHORT_EN.indexOf(l);
        if (shortIdx >= 0) return (chartDaysShort && chartDaysShort[shortIdx]) || l;
        const d = parseDDMMYYYY(l);
        if (d) return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
        const fallback = new Date(l);
        if (!isNaN(fallback.getTime())) return `${String(fallback.getDate()).padStart(2, '0')}/${String(fallback.getMonth() + 1).padStart(2, '0')}`;
        return l;
    });
};

const isOccupied = (checkIn, checkOut) => {
    const now = new Date(DEMO_TODAY);
    now.setHours(0, 0, 0, 0);
    const in_ = new Date(checkIn);
    in_.setHours(0, 0, 0, 0);
    const out = new Date(checkOut);
    out.setHours(23, 59, 59, 999);
    return now >= in_ && now <= out;
};

const SHARE_LINK_BASE = process.env.REACT_APP_SHARE_LINK_BASE_URL || 'https://mylink';

/** Format Date as YYYY-MM-DD for API. */
const toYYYYMMDD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

/** Get start and end dates (YYYY-MM-DD) for period. Uses real today. */
const getPeriodDateRange = (period) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (period === 'last7days') {
        const start = new Date(today);
        start.setDate(start.getDate() - 6);
        return { start: toYYYYMMDD(start), end: toYYYYMMDD(today) };
    }
    if (period === 'lastMonth') {
        const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const last = new Date(today.getFullYear(), today.getMonth(), 0);
        return { start: toYYYYMMDD(first), end: toYYYYMMDD(last) };
    }
    if (period === 'currentMonth') {
        const first = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start: toYYYYMMDD(first), end: toYYYYMMDD(today) };
    }
    return null;
};

const DashboardApartmentCard = ({ apartment, dictionary, apartmentCardDict, dateLocale, buildingAddress: buildingAddressProp, buildingId, expandedId, onExpandChange, onHouseUpdated }) => {
    const theme = useTheme();
    const { language } = useLanguageContext();
    const billBreakdownDict = multilingual[language]?.billBreakdown ?? multilingual.gr.billBreakdown;
    const periodicDict = multilingual[language]?.periodicConsumption ?? multilingual.gr.periodicConsumption;
    const displayAddress = buildingAddressProp != null ? buildingAddressProp : BUILDING_ADDRESS;
    const navigate = useNavigate();
    const isControlled = expandedId !== undefined && typeof onExpandChange === 'function';
    const [internalExpanded, setInternalExpanded] = useState(false);
    const effectiveExpanded = isControlled ? (apartment.id === expandedId) : internalExpanded;
    const setExpanded = isControlled ? (val) => { if (val) onExpandChange(apartment.id); else onExpandChange(null); } : setInternalExpanded;

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [copyLoading, setCopyLoading] = useState(false);
    const [lastWeekData, setLastWeekData] = useState(null);
    const [trendData, setTrendData] = useState(null);
    const [billingData, setBillingData] = useState(null);
    const [breakdownData, setBreakdownData] = useState(null);
    const [dataLoading, setDataLoading] = useState(false);
    const isFirstDayOfMonth = new Date().getDate() === 1;
    const [periodTrend, setPeriodTrend] = useState(() => (isFirstDayOfMonth ? 'last7days' : 'currentMonth'));
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [addDevicesModalOpen, setAddDevicesModalOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('usage');

    useEffect(() => {
        if (isFirstDayOfMonth && periodTrend === 'currentMonth') {
            setPeriodTrend('last7days');
        }
    }, [isFirstDayOfMonth, periodTrend]);

    // Fetch consumption when period changes (last7days, lastMonth, currentMonth; custom is handled by button)
    useEffect(() => {
        if (!effectiveExpanded || !apartment?.id || periodTrend === 'custom') return;
        let mounted = true;
        setDataLoading(true);
        let apiPeriod;
        if (periodTrend === 'last7days') {
            apiPeriod = 'lastweek';
        } else if (periodTrend === 'lastMonth') {
            apiPeriod = 'lastmonth';
        } else if (periodTrend === 'currentMonth') {
            const range = getPeriodDateRange('currentMonth');
            apiPeriod = range ? `period/${range.start}/${range.end}` : 'lastweek';
        } else {
            setDataLoading(false);
            return () => { mounted = false; };
        }
        getHousePeriodicConsumptions(apiPeriod, apartment.id)
            .then((response) => {
                if (!mounted) return;
                const consumption = Array.isArray(response?.consumption) ? response.consumption : [];
                const labels = Array.isArray(response?.labels) ? response.labels : [];
                const total = consumption.reduce((sum, v) => sum + (Number(v) || 0), 0);
                const data = { consumption, labels, total };
                setTrendData(data);
                if (periodTrend === 'last7days') setLastWeekData(data);
            })
            .catch(() => {
                if (!mounted) return;
                setTrendData(null);
                if (periodTrend === 'last7days') setLastWeekData(null);
            })
            .finally(() => {
                if (mounted) setDataLoading(false);
            });
        return () => { mounted = false; };
    }, [effectiveExpanded, apartment.id, periodTrend]);

    // Fetch cost/breakdown when period changes (aligned with consumption)
    useEffect(() => {
        if (!effectiveExpanded || !apartment?.id) return;
        let mounted = true;
        if (periodTrend === 'currentMonth') {
            getHouseBillingInfo(apartment.id)
                .then((data) => {
                    if (!mounted) return;
                    setBreakdownData(data || null);
                    setBillingData(data || null);
                })
                .catch(() => {
                    if (!mounted) return;
                    setBreakdownData(null);
                    setBillingData(null);
                });
            return () => { mounted = false; };
        }
        if (periodTrend === 'custom') {
            setBreakdownData(null);
            setBillingData(null);
            return () => { mounted = false; };
        }
        const range = getPeriodDateRange(periodTrend);
        if (!range) {
            return () => { mounted = false; };
        }
        getHouseCostByPeriod(range.start, range.end, apartment.id)
            .then((response) => {
                if (!mounted) return;
                const cumulative = response?.cumulative;
                if (cumulative) {
                    setBreakdownData(cumulative);
                    setBillingData({ cost: cumulative.total_bill_amount ?? cumulative.cost });
                } else {
                    setBreakdownData(null);
                    setBillingData(null);
                }
            })
            .catch(() => {
                if (!mounted) return;
                setBreakdownData(null);
                setBillingData(null);
            });
        return () => { mounted = false; };
    }, [effectiveExpanded, apartment.id, periodTrend]);

    const fetchCustomPeriod = () => {
        if (!apartment?.id || !customStart || !customEnd) return;
        const formattedPeriod = `period/${customStart}/${customEnd}`;
        setDataLoading(true);
        Promise.all([
            getHousePeriodicConsumptions(formattedPeriod, apartment.id),
            getHouseCostByPeriod(customStart, customEnd, apartment.id),
        ])
            .then(([consumptionRes, costRes]) => {
                const consumption = Array.isArray(consumptionRes?.consumption) ? consumptionRes.consumption : [];
                const labels = Array.isArray(consumptionRes?.labels) ? consumptionRes.labels : [];
                const total = consumption.reduce((sum, v) => sum + (Number(v) || 0), 0);
                setTrendData({ consumption, labels, total });
                const cumulative = costRes?.cumulative;
                if (cumulative) {
                    setBreakdownData(cumulative);
                    setBillingData({ cost: cumulative.total_bill_amount ?? cumulative.cost });
                } else {
                    setBreakdownData(null);
                    setBillingData(null);
                }
            })
            .catch(() => {
                setTrendData(null);
                setBreakdownData(null);
                setBillingData(null);
            })
            .finally(() => setDataLoading(false));
    };

    const handleCopyShareLink = async () => {
        setCopyLoading(true);
        try {
            const { data } = await axios.post(`${API_BASE}/api/apartment/${apartment.id}/share-token`);
            const token = data.access_token ?? data.token ?? data.jwt ?? '';
            // TODO: Add fix the URL when the backend is ready
            const path = buildingId != null ? `building/${buildingId}/apartment/${apartment.id}` : `apartment/${apartment.id}`;
            const url = `${SHARE_LINK_BASE}/${path}?token=${encodeURIComponent(token)}`;
            await navigator.clipboard.writeText(url);
            setSnackbarSeverity('success');
            setSnackbarMessage(dictionary.linkCopied ?? 'Link copied!');
            setSnackbarOpen(true);
        } catch (err) {
            setSnackbarSeverity('error');
            setSnackbarMessage(err.response?.data?.message || dictionary.shareLinkError || 'Failed to create share link');
            setSnackbarOpen(true);
        } finally {
            setCopyLoading(false);
        }
    };
    const totalConsumption = lastWeekData?.total ?? apartment.totalConsumption ?? 0;
    const effectiveTrendData = periodTrend === 'last7days' ? lastWeekData : trendData;
    const periodConsumptionTotal = effectiveTrendData?.total ?? 0;
    const periodLabel = periodTrend === 'last7days' ? (dictionary.last7Days ?? 'Last 7 Days') : periodTrend === 'lastMonth' ? (dictionary.lastMonth ?? 'Last Month') : periodTrend === 'currentMonth' ? (dictionary.currentMonth ?? 'Current Month') : (dictionary.custom ?? 'Custom');
    const chartConsumption = effectiveTrendData?.consumption?.length ? effectiveTrendData.consumption : (apartment.lastDaysConsumption || [0, 0, 0, 0, 0, 0, 0]);
    const rawChartLabels = effectiveTrendData?.labels?.length ? effectiveTrendData.labels : (apartment.lastDaysLabels || DAYS_SHORT_EN);
    const chartLabels = translateDayLabels(rawChartLabels, dictionary.chartDaysShort);
    // Cost from billing endpoint (data/cost/currentmonth), same as other project; fallback to estimate if not loaded
    const fallbackApartmentBillingCost = apartment?.billingCost != null ? Number(apartment.billingCost) : null;
    const resolvedBillingCost =
        billingData?.cost ?? billingData?.total ?? billingData?.billing_cost ?? fallbackApartmentBillingCost;
    const costLast7DaysEur = resolvedBillingCost != null ? Number(resolvedBillingCost).toFixed(2) : (totalConsumption * 0.16).toFixed(2);
    // Keep collapsed summary metrics stable (first-load values), regardless of expanded timeframe changes.
    const collapsedConsumption = Number(apartment?.totalConsumption ?? 0);
    const collapsedCostEur = fallbackApartmentBillingCost != null
        ? Number(fallbackApartmentBillingCost).toFixed(2)
        : (collapsedConsumption * 0.16).toFixed(2);
    const periodChipLabel = (() => {
        const monthIndex = new Date().getMonth();
        const isGreek = String(language || '').toLowerCase().startsWith('gr');
        if (isGreek) return `ΑΠΟ 1Η ${MONTHS_GR_GENITIVE[monthIndex]}`;
        return `SINCE ${MONTHS_EN[monthIndex]} 1ST`;
    })();
    const chartMaxIndex = chartConsumption.length ? chartConsumption.reduce((best, val, i) => (Number(chartConsumption[best]) < Number(val) ? i : best), 0) : -1;
    const chartOption = {
        xAxis: {
            type: 'category',
            data: chartLabels,
            axisLine: { show: true },
            axisLabel: { color: '#999', fontSize: 10 },
            axisTick: { show: false },
        },
        yAxis: {
            type: 'value',
            name: 'kWh',
            axisLine: { show: false },
            axisLabel: { color: '#999', fontSize: 10 },
            splitLine: { lineStyle: { type: 'dashed', color: '#eee' } },
        },
        series: [
            {
                data: chartConsumption,
                type: 'bar',
                itemStyle: {
                    color: (params) => (params.dataIndex === chartMaxIndex ? theme.palette.primary.main : theme.palette.secondary.main),
                },
                barWidth: chartLabels.length > 7 ? '70%' : '60%',
            },
        ],
        grid: { top: 28, bottom: 36, left: 52, right: 16 },
        tooltip: { trigger: 'axis', formatter: '{b}<br/>{c} kWh' },
    };

    const getBreakdownValue = (...fields) => {
        if (!breakdownData) return 0;
        for (const field of fields) {
            if (breakdownData[field] !== undefined && breakdownData[field] !== null) {
                return parseFloat(breakdownData[field]) || 0;
            }
        }
        return 0;
    };
    const breakdownElectricity = breakdownData
        ? getBreakdownValue('commission_charges') + getBreakdownValue('regulation_cost')
        : (billingData?.cost != null ? Number(billingData.cost) : totalConsumption * 0.16);
    const breakdownMunicipal = getBreakdownValue('municipal_cost');
    const breakdownErt = getBreakdownValue('ert_cost');
    const breakdownVat = getBreakdownValue('vat');
    const apiTotal = breakdownData != null ? (getBreakdownValue('total_bill_amount') || getBreakdownValue('cost')) : null;
    const breakdownTotal = apiTotal != null && apiTotal > 0
        ? apiTotal
        : breakdownData
            ? breakdownElectricity + getBreakdownValue('various_charges') + breakdownMunicipal + breakdownErt + breakdownVat
            : (billingData?.cost != null ? Number(billingData.cost) : totalConsumption * 0.16);

    return (
        <>
            <Accordion
                expanded={effectiveExpanded}
                onChange={(_, isExpanded) => setExpanded(isExpanded)}
                sx={{
                    '&:before': { display: 'none' },
                    boxShadow: 'none',
                    border: effectiveExpanded ? '2px solid' : '1px solid',
                    borderColor: effectiveExpanded ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    mb: 1.5,
                    overflow: 'hidden',
                    bgcolor: 'common.white',
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main', fontSize: 28 }} />}
                    sx={{ minHeight: 56, '& .MuiAccordionSummary-content': { alignItems: 'center', flex: 1 } }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minWidth: 0 }}>
                            <Chip
                                icon={<HomeIcon color="primary" sx={{ fontSize: 20 }} />}
                                label={apartment.name}
                                size="medium"
                                sx={{
                                    bgcolor: 'rgba(25, 118, 210, 0.08)',
                                    color: 'primary.main',
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'primary.light',
                                    boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.1)',
                                    '& .MuiChip-icon': { ml: 1, mr: 0.5 },
                                }}
                            />
                            <Chip
                                size="small"
                                icon={<LocationOnIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
                                label={displayAddress}
                                variant="outlined"
                                sx={{
                                    borderRadius: 2, p: 1, borderColor: 'primary.light',
                                    '& .MuiChip-icon': { ml: 0.5 }
                                }}
                            />
                            <Tooltip title={dictionary.copyShareLink ?? 'Copy share link'} placement="top" arrow>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); handleCopyShareLink(); }}
                                        disabled={copyLoading}
                                        sx={{ color: 'primary.main' }}
                                        aria-label={dictionary.copyShareLink ?? 'Copy share link'}
                                    >
                                        {copyLoading ? <CircularProgress size={20} color="inherit" /> : <LinkIcon />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                        {effectiveExpanded && (
                            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                <ToggleButtonGroup
                                    size="small"
                                    value={activeSection}
                                    exclusive
                                    onClick={(event) => event.stopPropagation()}
                                    onChange={(event, nextValue) => {
                                        event.stopPropagation();
                                        if (nextValue) setActiveSection(nextValue);
                                    }}
                                    sx={{
                                        display: 'inline-flex',
                                        bgcolor: 'grey.200',
                                        borderRadius: 5,
                                        p: 0.5,
                                        gap: 0,
                                        transition: 'all 0.25s ease',
                                        '& .MuiToggleButtonGroup-grouped': {
                                            border: 0,
                                            mx: 0,
                                        },
                                        '& .MuiToggleButton-root': {
                                            textTransform: 'none',
                                            minWidth: 0,
                                            px: 1.5,
                                            py: 0.75,
                                            borderRadius: '16px !important',
                                            bgcolor: 'transparent',
                                            color: 'grey.700',
                                            fontSize: '0.75rem',
                                            transition: 'all 0.25s ease',
                                            '&:hover': {
                                                bgcolor: 'rgba(255,255,255,0.5)',
                                            },
                                            '&.Mui-selected': {
                                                bgcolor: '#fff',
                                                color: 'primary.main',
                                                boxShadow: 1,
                                            },
                                            '&.Mui-selected:hover': {
                                                bgcolor: '#fff',
                                            },
                                        },
                                    }}
                                >
                                    <ToggleButton value="usage">
                                        {dictionary.usageDetailsTab || 'Usage Details'}
                                    </ToggleButton>
                                    <ToggleButton value="visits">
                                        {dictionary.visitsManagementTab || 'Visits Management'}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        )}
                    </Box>
                    {!effectiveExpanded && activeSection === 'usage' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 0.5 }}>
                            <Chip
                                label={periodChipLabel}
                                size="small"
                                sx={{
                                    height: 20,
                                    borderRadius: 1,
                                    bgcolor: '#eff6ff',
                                    border: '1px solid #bfdbfe',
                                    color: 'primary.main',
                                    '& .MuiChip-label': {
                                        px: 0.9,
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.2,
                                    },
                                }}
                            />
                            <Typography variant="body2" fontWeight={500} color="text.secondary">
                                €{collapsedCostEur}
                            </Typography>
                            <Typography variant="body2" color="text.disabled">|</Typography>
                            <Typography variant="body2" fontWeight={500} color="text.secondary">
                                {collapsedConsumption.toFixed(2)} kWh
                            </Typography>
                        </Box>
                    )}
                    {effectiveExpanded && (
                        <Typography variant="body2" sx={{ ml: 'auto', mr: 0.5, color: 'text.secondary', letterSpacing: '0.05em' }}>
                            {dictionary.clickToCollapse}
                        </Typography>
                    )}
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                    {activeSection === 'usage' ? (
                        <UsageDetails
                            dictionary={dictionary}
                            periodicDict={periodicDict}
                            billBreakdownDict={billBreakdownDict}
                            isFirstDayOfMonth={isFirstDayOfMonth}
                            periodTrend={periodTrend}
                            setPeriodTrend={setPeriodTrend}
                            setTrendData={setTrendData}
                            customStart={customStart}
                            setCustomStart={setCustomStart}
                            customEnd={customEnd}
                            setCustomEnd={setCustomEnd}
                            fetchCustomPeriod={fetchCustomPeriod}
                            dataLoading={dataLoading}
                            periodLabel={periodLabel}
                            periodConsumptionTotal={periodConsumptionTotal}
                            chartOption={chartOption}
                            breakdownTotal={breakdownTotal}
                            breakdownElectricity={breakdownElectricity}
                            breakdownMunicipal={breakdownMunicipal}
                            breakdownErt={breakdownErt}
                            breakdownVat={breakdownVat}
                            navigate={navigate}
                            buildingId={buildingId}
                            apartment={apartment}
                            setEditModalOpen={setEditModalOpen}
                            setAddDevicesModalOpen={setAddDevicesModalOpen}
                        />
                    ) : (
                        <VisitsManagement dictionary={dictionary} houseId={apartment?.id} />
                    )}
                </AccordionDetails>
            </Accordion >
            <EditHouseModal
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                houseId={apartment.id}
                apartmentName={apartment.name}
                apartmentAddress={displayAddress}
                dictionary={dictionary}
                onUpdated={onHouseUpdated}
            />
            <AddDevicesModal
                open={addDevicesModalOpen}
                onClose={() => setAddDevicesModalOpen(false)}
                houseId={apartment.id}
                dictionary={dictionary}
                onDevicesUpdated={onHouseUpdated}
            />
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                sx={{ mb: 5 }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)} variant="filled">
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default DashboardApartmentCard;
